import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Send, Image as ImageIcon, X, Lock, MessageSquare, RefreshCw, Crown } from "lucide-react";
import DashboardShell from "../components/shell/DashboardShell";
import { useAuth } from "../context/AuthContext";
import api, { API, formatApiError } from "../lib/api";

const MAX_BODY = 8000;

function planUnlocked(user) {
  if (!user) return false;
  if (user.role === "admin") return true;
  const p = (user.subscription_plan || "").toLowerCase();
  return p === "pro" || p === "enterprise";
}

function relTime(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 45) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.round(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

function initials(name = "?") {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");
}

function Avatar({ user, size = 36 }) {
  if (user?.avatar_data_uri) {
    return (
      <img
        src={user.avatar_data_uri}
        alt=""
        style={{ width: size, height: size }}
        className="rounded-full object-cover ring-2 ring-slate-200 shrink-0"
      />
    );
  }
  return (
    <div
      style={{ width: size, height: size, fontFamily: "Outfit" }}
      className="rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-bold shrink-0"
    >
      {initials(user?.full_name || user?.email || "?")}
    </div>
  );
}

function PlanGate() {
  return (
    <div className="max-w-2xl mx-auto mt-12" data-testid="messages-plan-gate">
      <div className="bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200 rounded-lg p-10 text-center">
        <div className="w-14 h-14 mx-auto rounded-full bg-amber-500 text-white flex items-center justify-center mb-4">
          <Lock className="w-6 h-6" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: "Outfit" }}>
          Internal team messaging is a Pro feature
        </h2>
        <p className="text-slate-700 max-w-md mx-auto">
          Upgrade to <span className="font-semibold">Pro</span> or <span className="font-semibold">Enterprise</span> to chat
          privately with your teammates inside RealtyVault — text, photos, and full thread history.
        </p>
        <div className="mt-6 flex items-center justify-center gap-2">
          <a
            href="/dashboard/billing"
            data-testid="messages-upgrade-cta"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors"
          >
            <Crown className="w-4 h-4" /> Unlock with Pro
          </a>
          <a
            href="/dashboard/help"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-white border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50"
          >
            Learn more
          </a>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ onlyMember }) {
  return (
    <div className="flex-1 flex items-center justify-center text-slate-500" data-testid="messages-empty-state">
      <div className="text-center max-w-sm">
        <div className="w-12 h-12 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3">
          <MessageSquare className="w-5 h-5 text-slate-500" />
        </div>
        {onlyMember ? (
          <>
            <div className="font-semibold text-slate-700">No teammates yet</div>
            <p className="text-sm mt-1">
              Invite a teammate from{" "}
              <a href="/dashboard/settings" className="text-slate-900 underline">
                Settings → Team
              </a>{" "}
              to start chatting.
            </p>
          </>
        ) : (
          <>
            <div className="font-semibold text-slate-700">Pick a teammate</div>
            <p className="text-sm mt-1">Choose someone from the left to open a private conversation.</p>
          </>
        )}
      </div>
    </div>
  );
}

export default function Messages() {
  const { user } = useAuth();
  const unlocked = planUnlocked(user);
  const [searchParams, setSearchParams] = useSearchParams();
  const presetUserId = searchParams.get("u");

  const [meta, setMeta] = useState(null);
  const [activeId, setActiveId] = useState(presetUserId || null);
  const [messages, setMessages] = useState([]);
  const [otherUser, setOtherUser] = useState(null);
  const [body, setBody] = useState("");
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [mentionState, setMentionState] = useState({ open: false, query: "", start: -1 });
  const [pendingMentionIds, setPendingMentionIds] = useState([]);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const fileRef = useRef(null);
  const scrollRef = useRef(null);
  const evtRef = useRef(null);
  const inputRef = useRef(null);

  const loadMembers = useCallback(async () => {
    try {
      const res = await api.get("/messages/account-members");
      setMeta(res.data);
      if (!activeId && res.data.members.length > 0) {
        setActiveId(res.data.members[0].id);
      }
    } catch (e) {
      if (e?.response?.status !== 402) toast.error(formatApiError(e?.response?.data?.detail));
    }
  }, [activeId]);

  const loadThread = useCallback(async (otherId, opts = {}) => {
    if (!otherId) return;
    if (!opts.silent) setLoadingThread(true);
    try {
      const res = await api.get(`/messages/threads/${otherId}`);
      setMessages(res.data.messages || []);
      setOtherUser(res.data.other || null);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      if (!opts.silent) setLoadingThread(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    if (unlocked) loadMembers();
  }, [unlocked, loadMembers]);

  // Switch threads
  useEffect(() => {
    if (activeId) {
      loadThread(activeId);
      // Reflect in URL for shareability + browser back/forward
      const sp = new URLSearchParams(searchParams);
      sp.set("u", activeId);
      setSearchParams(sp, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, activeId]);

  // SSE live stream
  useEffect(() => {
    if (!unlocked) return;
    const tok = localStorage.getItem("rv_token");
    if (!tok) return;
    const url = `${API}/messages/stream?token=${encodeURIComponent(tok)}`;
    const es = new EventSource(url);
    evtRef.current = es;
    es.addEventListener("message", (ev) => {
      try {
        const m = JSON.parse(ev.data);
        // If message is part of the open thread, append it
        const callerId = meta?.me?.id;
        const otherId = activeId;
        const isInOpenThread =
          (m.sender_id === callerId && m.recipient_id === otherId) ||
          (m.sender_id === otherId && m.recipient_id === callerId);
        if (isInOpenThread) {
          setMessages((prev) => {
            if (prev.some((x) => x.id === m.id)) return prev;
            return [...prev, m];
          });
          // If it was inbound, mark thread read silently
          if (m.recipient_id === callerId && otherId) {
            api.get(`/messages/threads/${otherId}`).catch(() => {});
          }
        }
        // Always refresh members panel for unread counts
        loadMembers();
      } catch {
        // ignore parse errors
      }
    });
    es.onerror = () => {
      // Auto-reconnect handled by EventSource; nothing to do.
    };
    return () => {
      es.close();
      evtRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unlocked, meta?.me?.id, activeId]);

  const onPickFiles = (files) => {
    if (!files || !files.length) return;
    const remaining = 4 - pendingAttachments.length;
    const sliced = Array.from(files).slice(0, Math.max(0, remaining));
    setPendingAttachments((prev) => [
      ...prev,
      ...sliced.map((f) => ({
        local_id: `${f.name}-${f.size}-${Math.random().toString(36).slice(2, 8)}`,
        file: f,
        preview: URL.createObjectURL(f),
        uploading: false,
      })),
    ]);
  };

  const removePending = (local_id) => {
    setPendingAttachments((prev) => {
      const target = prev.find((p) => p.local_id === local_id);
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return prev.filter((p) => p.local_id !== local_id);
    });
  };

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      pendingAttachments.forEach((p) => p.preview && URL.revokeObjectURL(p.preview));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = async () => {
    if (!activeId) return;
    const trimmed = body.trim();
    if (!trimmed && pendingAttachments.length === 0) {
      toast.error("Type a message or attach a photo.");
      return;
    }
    setSending(true);
    try {
      // Upload pending attachments first
      const uploaded = [];
      for (const att of pendingAttachments) {
        const fd = new FormData();
        fd.append("file", att.file);
        try {
          const res = await api.post("/messages/upload", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          uploaded.push(res.data);
        } catch (e) {
          toast.error(`Upload failed: ${formatApiError(e?.response?.data?.detail)}`);
          setSending(false);
          return;
        }
      }
      // Compute final mentions list — only those whose name is still in the body
      const finalMentions = (() => {
        if (!pendingMentionIds.length || !meta) return [];
        const out = new Set();
        for (const uid of pendingMentionIds) {
          const m = (meta.members || []).find((x) => x.id === uid);
          const label = m?.full_name || "";
          if (label && trimmed.includes(`@${label}`)) out.add(uid);
        }
        return [...out];
      })();
      const res = await api.post("/messages", {
        recipient_id: activeId,
        body: trimmed,
        attachments: uploaded,
        mentions: finalMentions,
      });
      setMessages((prev) => {
        if (prev.some((x) => x.id === res.data.id)) return prev;
        // Strip the synthetic welcome on first real send
        const filtered = prev.filter((x) => !x.is_system);
        return [...filtered, res.data];
      });
      setBody("");
      setPendingMentionIds([]);
      setMentionState({ open: false, query: "", start: -1 });
      pendingAttachments.forEach((p) => p.preview && URL.revokeObjectURL(p.preview));
      setPendingAttachments([]);
      // Refresh sidebar metadata in background
      loadMembers();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  // -------- @-mention autocomplete --------
  const allCandidates = useMemo(() => {
    const list = [...(meta?.members || [])];
    return list;
  }, [meta]);

  const filteredCandidates = useMemo(() => {
    if (!mentionState.open) return [];
    const q = mentionState.query.toLowerCase();
    if (!q) return allCandidates.slice(0, 8);
    return allCandidates
      .filter(
        (c) =>
          (c.full_name || "").toLowerCase().includes(q) ||
          (c.email || "").toLowerCase().includes(q),
      )
      .slice(0, 8);
  }, [mentionState, allCandidates]);

  const onBodyChange = (val) => {
    setBody(val);
    const ta = inputRef.current;
    if (!ta) return;
    const cursor = ta.selectionStart || val.length;
    // Find the last "@" before the cursor that's at start-of-text or after whitespace
    let i = cursor - 1;
    let token = "";
    let start = -1;
    while (i >= 0) {
      const ch = val[i];
      if (ch === "@") {
        const prev = i > 0 ? val[i - 1] : " ";
        if (i === 0 || /\s/.test(prev)) {
          start = i;
        }
        break;
      }
      if (/\s/.test(ch)) break;
      token = ch + token;
      i -= 1;
    }
    if (start >= 0) {
      setMentionState({ open: true, query: token, start });
      setHighlightIdx(0);
    } else if (mentionState.open) {
      setMentionState({ open: false, query: "", start: -1 });
    }
  };

  const pickMention = (cand) => {
    if (!cand) return;
    const ta = inputRef.current;
    const cursor = ta?.selectionStart ?? body.length;
    const start = mentionState.start;
    if (start < 0) return;
    const before = body.slice(0, start);
    const after = body.slice(cursor);
    const insert = `@${cand.full_name} `;
    const next = before + insert + after;
    setBody(next);
    setPendingMentionIds((prev) => (prev.includes(cand.id) ? prev : [...prev, cand.id]));
    setMentionState({ open: false, query: "", start: -1 });
    // Restore caret after the inserted token
    requestAnimationFrame(() => {
      const pos = (before + insert).length;
      if (ta) {
        ta.focus();
        ta.setSelectionRange(pos, pos);
      }
    });
  };

  const onKey = (e) => {
    if (mentionState.open && filteredCandidates.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightIdx((i) => (i + 1) % filteredCandidates.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightIdx((i) => (i - 1 + filteredCandidates.length) % filteredCandidates.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        pickMention(filteredCandidates[highlightIdx]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setMentionState({ open: false, query: "", start: -1 });
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!sending) send();
    }
  };

  const refreshAll = async () => {
    setRefreshing(true);
    await Promise.all([loadMembers(), activeId ? loadThread(activeId, { silent: true }) : Promise.resolve()]);
    setRefreshing(false);
  };

  const memberById = useMemo(() => {
    const m = {};
    (meta?.members || []).forEach((mem) => (m[mem.id] = mem));
    return m;
  }, [meta]);

  if (!unlocked) {
    return (
      <DashboardShell>
        <div className="px-8 py-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: "Outfit" }}>
            Team Messages
          </h1>
          <p className="text-slate-600 mb-6">Private chat for your account teammates.</p>
          <PlanGate />
        </div>
      </DashboardShell>
    );
  }

  const callerId = meta?.me?.id;

  return (
    <DashboardShell>
      <div className="px-8 py-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }} data-testid="messages-page-title">
              Team Messages
            </h1>
            <p className="text-slate-600 text-sm mt-1">
              Private chat for {meta?.me?.is_primary ? "you and your teammates" : "your account team"}.
            </p>
          </div>
          <button
            onClick={refreshAll}
            data-testid="messages-refresh-btn"
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>

        <div
          className="grid grid-cols-12 gap-4 bg-white border border-slate-200 rounded-lg overflow-hidden"
          style={{ minHeight: "calc(100vh - 220px)" }}
          data-testid="messages-shell"
        >
          {/* LEFT: members list */}
          <aside className="col-span-12 md:col-span-4 lg:col-span-3 border-r border-slate-800 flex flex-col bg-slate-900 text-white">
            <div className="p-3 border-b border-slate-800 text-xs uppercase tracking-widest text-white font-bold flex items-center justify-between">
              <span>Teammates</span>
              {meta?.total_unread > 0 && (
                <span
                  data-testid="messages-total-unread"
                  className="bg-rose-500 text-white text-[10px] rounded-full px-2 py-0.5 font-bold"
                >
                  {meta.total_unread}
                </span>
              )}
            </div>
            <div className="flex-1 overflow-y-auto" data-testid="messages-member-list">
              {(meta?.members || []).length === 0 && (
                <div className="p-4 text-sm text-slate-300">
                  No teammates yet. Invite from{" "}
                  <a href="/dashboard/settings" className="text-white underline">
                    Settings
                  </a>
                  .
                </div>
              )}
              {(meta?.members || []).map((m) => {
                const active = m.id === activeId;
                return (
                  <button
                    key={m.id}
                    data-testid={`messages-member-${m.id}`}
                    onClick={() => setActiveId(m.id)}
                    className={`w-full text-left px-3 py-3 flex items-start gap-3 border-l-2 transition-colors ${
                      active
                        ? "bg-white/10 border-l-white"
                        : "border-l-transparent hover:bg-white/5"
                    }`}
                  >
                    <Avatar user={m} size={36} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-white text-sm truncate">{m.full_name}</span>
                        {m.unread_count > 0 && (
                          <span
                            data-testid={`messages-member-unread-${m.id}`}
                            className="bg-rose-500 text-white text-[10px] rounded-full px-2 py-0.5 font-bold shrink-0"
                          >
                            {m.unread_count}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-300 truncate">
                        {m.is_primary ? "Account owner · " : ""}
                        {m.email}
                      </div>
                      {m.last_message && (
                        <div className="text-xs text-slate-200 mt-1 truncate">
                          {m.last_message.from_me ? "You: " : ""}
                          {m.last_message.body || "📷 Photo"}
                          <span className="text-slate-400"> · {relTime(m.last_message.created_at)}</span>
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </aside>

          {/* RIGHT: thread + composer */}
          <section className="col-span-12 md:col-span-8 lg:col-span-9 flex flex-col">
            {!activeId ? (
              <EmptyState onlyMember={!meta?.members?.length} />
            ) : (
              <>
                {/* Thread header */}
                <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-3" data-testid="messages-thread-header">
                  <Avatar user={otherUser || memberById[activeId]} size={40} />
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900">
                      {otherUser?.full_name || memberById[activeId]?.full_name || "Teammate"}
                    </div>
                    <div className="text-xs text-slate-500">{otherUser?.email || memberById[activeId]?.email}</div>
                  </div>
                </div>

                {/* Messages */}
                <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-slate-50/50" data-testid="messages-thread">
                  {loadingThread && messages.length === 0 && (
                    <div className="text-sm text-slate-500">Loading…</div>
                  )}
                  {messages.map((m) => {
                    const fromMe = m.sender_id === callerId;
                    const isSystem = m.is_system || m.sender_id === "system";
                    if (isSystem) {
                      return (
                        <div key={m.id} className="flex justify-center" data-testid="messages-system-bubble">
                          <div className="max-w-md text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-md px-4 py-2 whitespace-pre-line text-center">
                            {m.body}
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div
                        key={m.id}
                        className={`flex ${fromMe ? "justify-end" : "justify-start"}`}
                        data-testid={`messages-bubble-${m.id}`}
                      >
                        <div
                          className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap shadow-sm ${
                            fromMe
                              ? "bg-slate-900 text-white rounded-br-sm"
                              : "bg-white border border-slate-200 text-slate-900 rounded-bl-sm"
                          }`}
                        >
                          <MessageBody
                            text={m.body}
                            mentions={m.mentions}
                            members={[...(meta?.members || []), meta?.me ? { id: meta.me.id, full_name: meta.me.full_name, email: meta.me.email } : null].filter(Boolean)}
                            myId={callerId}
                            onMentionClick={(uid) => {
                              if (uid !== callerId) setActiveId(uid);
                            }}
                            fromMe={fromMe}
                          />
                          {(m.attachments || []).length > 0 && (
                            <div className={`mt-2 grid gap-2 ${m.attachments.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
                              {m.attachments.map((a) => (
                                <AttachmentThumb key={a.id} msgId={m.id} attachment={a} />
                              ))}
                            </div>
                          )}
                          <div className={`text-[10px] mt-1 ${fromMe ? "text-slate-300" : "text-slate-400"}`}>
                            {relTime(m.created_at)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Composer */}
                <div className="border-t border-slate-100 p-3 bg-white" data-testid="messages-composer">
                  {pendingAttachments.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {pendingAttachments.map((p) => (
                        <div key={p.local_id} className="relative w-20 h-20 rounded-md overflow-hidden border border-slate-200">
                          <img src={p.preview} alt="" className="w-full h-full object-cover" />
                          <button
                            onClick={() => removePending(p.local_id)}
                            className="absolute top-0 right-0 bg-slate-900/80 text-white p-0.5 rounded-bl-md"
                            data-testid={`messages-attachment-remove-${p.local_id}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex items-end gap-2">
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      data-testid="messages-attach-btn"
                      className="p-2 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                      title="Attach photo"
                    >
                      <ImageIcon className="w-5 h-5" />
                    </button>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        onPickFiles(e.target.files);
                        e.target.value = "";
                      }}
                      data-testid="messages-file-input"
                    />
                    <div className="flex-1 relative">
                      <textarea
                        ref={inputRef}
                        value={body}
                        onChange={(e) => onBodyChange(e.target.value.slice(0, MAX_BODY))}
                        onKeyDown={onKey}
                        placeholder="Type a message — @mention to tag a teammate, Enter to send"
                        rows={1}
                        data-testid="messages-input"
                        className="w-full resize-none rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 max-h-32"
                      />
                      {mentionState.open && filteredCandidates.length > 0 && (
                        <div
                          data-testid="messages-mention-dropdown"
                          className="absolute bottom-full left-0 mb-2 w-72 max-h-60 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg z-30"
                        >
                          <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-slate-500 font-bold border-b border-slate-100">
                            Mention a teammate
                          </div>
                          {filteredCandidates.map((c, idx) => (
                            <button
                              key={c.id}
                              data-testid={`messages-mention-option-${c.id}`}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                pickMention(c);
                              }}
                              onMouseEnter={() => setHighlightIdx(idx)}
                              className={`w-full text-left px-3 py-2 flex items-center gap-2 ${
                                idx === highlightIdx ? "bg-slate-100" : "hover:bg-slate-50"
                              }`}
                            >
                              <Avatar user={c} size={28} />
                              <div className="min-w-0">
                                <div className="text-sm font-semibold text-slate-900 truncate">{c.full_name}</div>
                                <div className="text-xs text-slate-500 truncate">{c.email}</div>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={send}
                      disabled={sending || (!body.trim() && pendingAttachments.length === 0)}
                      data-testid="messages-send-btn"
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed"
                    >
                      <Send className="w-4 h-4" /> {sending ? "…" : "Send"}
                    </button>
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </DashboardShell>
  );
}

function AttachmentThumb({ msgId, attachment }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let blobUrl = null;
    (async () => {
      try {
        const res = await api.get(`/messages/attachment/${msgId}/${attachment.id}`, {
          responseType: "blob",
        });
        if (cancelled) return;
        blobUrl = URL.createObjectURL(res.data);
        setSrc(blobUrl);
      } catch {
        setErr(true);
      }
    })();
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [msgId, attachment.id]);

  if (err) {
    return (
      <div className="bg-rose-50 text-rose-700 text-xs p-2 rounded-md">Could not load image.</div>
    );
  }
  if (!src) {
    return <div className="bg-slate-200 animate-pulse h-32 rounded-md" />;
  }
  return (
    <a href={src} target="_blank" rel="noopener noreferrer" data-testid={`messages-attachment-thumb-${attachment.id}`}>
      <img src={src} alt={attachment.filename} className="w-full max-h-48 object-cover rounded-md" />
    </a>
  );
}


function MessageBody({ text, mentions, members, myId, onMentionClick, fromMe }) {
  if (!text) return null;
  const ids = Array.from(mentions || []);
  if (ids.length === 0) return <span data-testid="messages-bubble-body">{text}</span>;

  // Build a map of full_name -> user object (only for ids in this message)
  const tagged = ids
    .map((uid) => members.find((m) => m.id === uid))
    .filter((m) => m && m.full_name);
  if (tagged.length === 0) return <span data-testid="messages-bubble-body">{text}</span>;

  // Sort longest names first so "@Mate One" matches before "@Mate"
  tagged.sort((a, b) => (b.full_name?.length || 0) - (a.full_name?.length || 0));

  // Build a single regex that matches any of the tagged names prefixed by "@"
  const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const pattern = new RegExp(`@(${tagged.map((t) => escape(t.full_name)).join("|")})`, "g");

  const parts = [];
  let lastIndex = 0;
  let match;
  let key = 0;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(<span key={`t-${key++}`}>{text.slice(lastIndex, match.index)}</span>);
    }
    const name = match[1];
    const u = tagged.find((t) => t.full_name === name);
    const isMe = u && u.id === myId;
    parts.push(
      <button
        key={`m-${key++}`}
        type="button"
        data-testid={`messages-mention-token-${u?.id}`}
        onClick={(e) => {
          e.stopPropagation();
          if (u && onMentionClick) onMentionClick(u.id);
        }}
        className={`inline px-1 rounded font-semibold ${
          fromMe
            ? "bg-white/15 text-white hover:bg-white/25"
            : isMe
              ? "bg-amber-100 text-amber-900 hover:bg-amber-200"
              : "bg-sky-100 text-sky-800 hover:bg-sky-200"
        }`}
      >
        @{name}
      </button>,
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(<span key={`t-${key++}`}>{text.slice(lastIndex)}</span>);
  }
  return <span data-testid="messages-bubble-body">{parts}</span>;
}
